import type { Graphics } from 'pixi.js';
import { COLORS, LIFT_PARTS } from '../theme.js';

/**
 * The parts the player has fitted, drawn on the lift in play (ADR-0021).
 *
 * The garage shows what a part is; this is where the player sees they have it.
 * Each tier looks like its garage picture, small: the motor sits on the roof
 * where the cable meets the car, the doors carry their tier's pattern while
 * shut, and the six-seat cabin gets its brass trim. Nothing here goes inside
 * the cabin where riders sit — on a 12-floor level their numbers are already
 * small enough (KNOWN_ISSUES) — and nothing here is a floor's colour.
 */

/** The hoist's sheave radius, the gap between its two ropes, and the lay of their strands. */
const SHEAVE_R = 11;
const ROPE_GAP = 5;
const LAY = 6;

/**
 * The hoist: a steel sheave at the head of the shaft, and two ropes down to the
 * car. The strands' lay marks belong to the rope and the rope to the car, so
 * they move exactly as the car does — up into the sheave as it climbs, out of
 * it as it falls — and the sheave turns through the same distance. When the
 * car is still, so is all of it. Drawn first, so the car passes in front.
 *
 * `travel` is how far the car has risen from the ground, in px.
 */
export function paintHoist(
  g: Graphics,
  cx: number,
  topY: number,
  apexY: number,
  travel: number,
  cable = 0,
): void {
  // Hung below a steel beam, so the head of the shaft reads as machinery.
  const sy = topY + 4 + SHEAVE_R;
  if (apexY > sy) paintRopes(g, cx, sy, apexY, cable);
  // The beam and the axle's bracket, then the sheave, turning as the rope runs over it.
  g.rect(cx - SHEAVE_R - 7, topY, (SHEAVE_R + 7) * 2, 4)
    .fill({ color: LIFT_PARTS.iron })
    .stroke({ width: 1, color: COLORS.ink });
  g.rect(cx - 1.5, topY + 4, 3, SHEAVE_R).fill({ color: LIFT_PARTS.iron });
  g.circle(cx, sy, SHEAVE_R)
    .fill({ color: LIFT_PARTS.steel })
    .stroke({ width: 1.2, color: COLORS.ink });
  g.circle(cx, sy, SHEAVE_R - 2.4).stroke({ width: 1, color: LIFT_PARTS.steelDark });
  const turn = travel / SHEAVE_R;
  for (let i = 0; i < 4; i += 1) {
    const a = turn + (Math.PI / 2) * i;
    g.moveTo(cx, sy)
      .lineTo(cx + Math.cos(a) * (SHEAVE_R - 2.6), sy + Math.sin(a) * (SHEAVE_R - 2.6))
      .stroke({ width: 1.4, color: LIFT_PARTS.iron });
  }
  g.circle(cx, sy, 2.4).fill({ color: LIFT_PARTS.iron });
}

/**
 * The ropes by the cable fitted (ADR-0033), as the garage pictures them: two
 * steel ropes, thicker ones, three compacted strands, aramid with a yellow
 * tracer, carbon, and a carbon belt with brass clamps.
 */
const ROPES: readonly {
  readonly count: number;
  readonly width: number;
  readonly colour: number;
  readonly lay: number;
}[] = [
  { count: 2, width: 1.8, colour: LIFT_PARTS.steelDark, lay: LIFT_PARTS.iron },
  { count: 2, width: 2.4, colour: LIFT_PARTS.steelDark, lay: LIFT_PARTS.iron },
  { count: 3, width: 2.2, colour: LIFT_PARTS.steelDark, lay: COLORS.ink },
  { count: 2, width: 2.6, colour: LIFT_PARTS.iron, lay: LIFT_PARTS.glow },
  { count: 3, width: 2.4, colour: LIFT_PARTS.iron, lay: LIFT_PARTS.steel },
];

function paintRopes(g: Graphics, cx: number, sy: number, apexY: number, cable: number): void {
  if (cable >= 5) {
    // The belt: one flat band, its clamps riding with the car.
    g.rect(cx - 4, sy, 8, apexY - sy).fill({ color: LIFT_PARTS.iron });
    g.moveTo(cx - 1.5, sy)
      .lineTo(cx - 1.5, apexY)
      .stroke({ width: 0.8, color: LIFT_PARTS.steel });
    for (let y = apexY - 6; y > sy + 2; y -= LAY * 4) {
      g.rect(cx - 5, y - 1.5, 10, 3).fill({ color: LIFT_PARTS.brass });
    }
    return;
  }
  const rope = ROPES[Math.max(0, cable)] as (typeof ROPES)[number];
  const span = rope.count === 3 ? ROPE_GAP * 1.6 : ROPE_GAP;
  for (let i = 0; i < rope.count; i += 1) {
    const rx = cx - span / 2 + (span * i) / (rope.count - 1);
    g.moveTo(rx, sy).lineTo(rx, apexY).stroke({ width: rope.width, color: rope.colour });
    g.moveTo(rx - 0.4, sy)
      .lineTo(rx - 0.4, apexY)
      .stroke({ width: 0.6, color: LIFT_PARTS.steel });
    for (let y = apexY - 2; y > sy + 1; y -= LAY) {
      g.moveTo(rx - 0.9, y)
        .lineTo(rx + 0.9, y - 1.6)
        .stroke({ width: 0.8, color: rope.lay, alpha: 0.6 });
    }
  }
}

/**
 * The motor where the cable meets the roof. Returns how far above the roof's
 * apex it reaches, so the capacity label can sit clear of it.
 */
export function paintMotor(g: Graphics, cx: number, apexY: number, tier: number): number {
  if (tier <= 0) return 0;
  if (tier === 1) {
    // Geared: a brass gear on the roof.
    for (let i = 0; i < 8; i += 1) {
      const a = (Math.PI * 2 * i) / 8;
      g.circle(cx + Math.cos(a) * 6, apexY - 1 + Math.sin(a) * 6, 1.6).fill({
        color: LIFT_PARTS.brass,
      });
    }
    g.circle(cx, apexY - 1, 5.5)
      .fill({ color: LIFT_PARTS.brass })
      .stroke({ width: 1.2, color: COLORS.ink });
    g.circle(cx, apexY - 1, 1.8).fill({ color: COLORS.ink });
    return 6;
  }
  if (tier >= 3) return paintBigMotor(g, cx, apexY, tier);
  // High-speed: an iron capsule with brass stripes and a lit lamp.
  g.roundRect(cx - 13, apexY - 8, 26, 9, 4.5)
    .fill({ color: LIFT_PARTS.iron })
    .stroke({ width: 1.2, color: COLORS.ink });
  for (const dx of [-7, -2]) {
    g.moveTo(cx + dx, apexY)
      .lineTo(cx + dx + 3, apexY - 7)
      .lineTo(cx + dx + 5, apexY - 7)
      .lineTo(cx + dx + 2, apexY)
      .closePath()
      .fill({ color: LIFT_PARTS.brass });
  }
  g.circle(cx + 7, apexY - 3.5, 2).fill({ color: LIFT_PARTS.glow });
  return 9;
}

/**
 * Tiers 3–5 (ADR-0033): a bigger capsule in a brass collar (3), with a turbine
 * and twin lamps (4), and a lit magnetic coil (5).
 */
function paintBigMotor(g: Graphics, cx: number, apexY: number, tier: number): number {
  if (tier >= 5) {
    g.ellipse(cx, apexY - 5, 19, 8).fill({ color: LIFT_PARTS.glow, alpha: 0.3 });
  }
  g.roundRect(cx - 15, apexY - 10, 30, 11, 5.5)
    .fill({ color: LIFT_PARTS.iron })
    .stroke({ width: 1.2, color: COLORS.ink });
  if (tier === 3) {
    g.rect(cx - 3, apexY - 11, 6, 13)
      .fill({ color: LIFT_PARTS.brass })
      .stroke({ width: 1, color: COLORS.ink });
    g.circle(cx + 9, apexY - 4.5, 2.4).fill({ color: LIFT_PARTS.glow });
    return 11;
  }
  if (tier === 4) {
    g.circle(cx - 4, apexY - 4.5, 4)
      .fill({ color: LIFT_PARTS.steelDark })
      .stroke({ width: 1, color: COLORS.ink });
    for (let i = 0; i < 4; i += 1) {
      const a = (Math.PI / 2) * i + Math.PI / 4;
      g.moveTo(cx - 4, apexY - 4.5)
        .lineTo(cx - 4 + Math.cos(a) * 3.4, apexY - 4.5 + Math.sin(a) * 3.4)
        .stroke({ width: 1.2, color: LIFT_PARTS.brass });
    }
    g.circle(cx + 6, apexY - 7, 1.8).fill({ color: LIFT_PARTS.glow });
    g.circle(cx + 11, apexY - 7, 1.8).fill({ color: LIFT_PARTS.glow });
    return 11;
  }
  for (const dx of [-9, -3, 3, 9]) {
    g.moveTo(cx + dx, apexY - 9)
      .lineTo(cx + dx, apexY)
      .stroke({ width: 2.2, color: LIFT_PARTS.glow });
  }
  return 12;
}

/**
 * Motion streaks beside a fast car, trailing behind it. Longer for a faster
 * motor; none for the stock one, which is exactly as fast as it looks.
 */
export function paintStreaks(
  g: Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  tier: number,
  direction: number,
): void {
  if (tier <= 0 || direction === 0) return;
  const length = 8 + tier * 7;
  // Trailing: below a car going up, above a car going down.
  const from = direction > 0 ? y + height - 6 : y + 6;
  const to = direction > 0 ? from + length : from - length;
  for (const sx of [x - 4.5, x + width + 3.5]) {
    g.moveTo(sx, from)
      .lineTo(sx, to)
      .stroke({ width: 1.5, color: COLORS.ink, alpha: 0.3, cap: 'round' });
  }
}

/**
 * How a building's lift is finished (ADR-0027). Every building has its own
 * lift, and its doors are made the way the building is: the tower's steel, the
 * hotel's brass, the station's wide hazard-edged doors, the skyscraper's glass.
 * The tiers still read as tiers within each — a part looks like what it does.
 */
export type LiftLook = 'steel' | 'brass' | 'wide' | 'glass';

export function liftLookOf(buildingId: string | undefined): LiftLook {
  switch (buildingId) {
    case 'grand-hotel':
      return 'brass';
    case 'transit-centre':
      return 'wide';
    case 'skyline-tower':
      return 'glass';
    default:
      return 'steel';
  }
}

/** One door panel's face, by look and tier. Only drawn while the panel is wide enough to read. */
export function paintDoorPanel(
  g: Graphics,
  px: number,
  py: number,
  pw: number,
  ph: number,
  tier: number,
  side: 'left' | 'right',
  look: LiftLook = 'steel',
): void {
  if (pw < 8) return;
  if (tier >= 3) {
    // Tiers 3–5 (ADR-0033): the best doors, finished further.
    paintDoorPanel(g, px, py, pw, ph, 2, side, look);
    paintDoorFinish(g, px, py, pw, ph, tier);
    return;
  }
  if (look !== 'steel') {
    const paint =
      look === 'brass' ? paintBrassPanel : look === 'wide' ? paintWidePanel : paintGlassPanel;
    paint(g, px, py, pw, ph, tier, side);
    return;
  }
  if (tier <= 0) {
    // Manual: a folding scissor gate.
    g.moveTo(px, py)
      .lineTo(px + pw, py + ph)
      .moveTo(px + pw, py)
      .lineTo(px, py + ph)
      .stroke({ width: 1.2, color: LIFT_PARTS.steelDark, alpha: 0.6 });
    return;
  }
  if (tier === 1) {
    // Powered: plain sliding panels with a window.
    if (pw < 12) return;
    g.roundRect(px + pw * 0.3, py + ph * 0.14, pw * 0.4, ph * 0.38, 2)
      .fill({ color: LIFT_PARTS.glass })
      .stroke({ width: 1, color: COLORS.ink, alpha: 0.6 });
    return;
  }
  // Express: brass-edged, with a chevron pointing the way the panel opens.
  g.rect(px + 1.5, py + 1.5, pw - 3, ph - 3).stroke({ width: 2, color: LIFT_PARTS.brass });
  if (pw < 12) return;
  const tip = side === 'left' ? px + pw * 0.32 : px + pw * 0.68;
  const tail = side === 'left' ? px + pw * 0.62 : px + pw * 0.38;
  const mid = py + ph * 0.5;
  const span = Math.min(ph * 0.14, 7);
  g.moveTo(tail, mid - span)
    .lineTo(tip, mid)
    .lineTo(tail, mid + span)
    .stroke({ width: 2.2, color: LIFT_PARTS.iron, cap: 'round', join: 'round' });
}

/** Brass rivets along the head (3), a lit sill (4), and a faint glow on the panel (5). */
function paintDoorFinish(
  g: Graphics,
  px: number,
  py: number,
  pw: number,
  ph: number,
  tier: number,
): void {
  for (const t of [0.25, 0.5, 0.75]) {
    g.circle(px + pw * t, py + 2.5, 1.1).fill({ color: LIFT_PARTS.brass });
  }
  if (tier >= 4) {
    g.rect(px + 1, py + ph - 3, pw - 2, 2).fill({ color: LIFT_PARTS.glow });
  }
  if (tier >= 5) {
    g.rect(px + 1, py + 1, pw - 2, ph - 2).fill({ color: LIFT_PARTS.glow, alpha: 0.14 });
  }
}

/** The hotel's: a brass gate, brass doors, then brass with a sunburst fanning from the seam. */
function paintBrassPanel(
  g: Graphics,
  px: number,
  py: number,
  pw: number,
  ph: number,
  tier: number,
  side: 'left' | 'right',
): void {
  if (tier <= 0) {
    g.moveTo(px, py)
      .lineTo(px + pw, py + ph)
      .moveTo(px + pw, py)
      .lineTo(px, py + ph)
      .stroke({ width: 1.6, color: LIFT_PARTS.brass });
    return;
  }
  g.rect(px + 1.5, py + 1.5, pw - 3, ph - 3).stroke({
    width: tier === 1 ? 1.5 : 2.2,
    color: LIFT_PARTS.brass,
  });
  if (tier === 1) {
    g.moveTo(px + 3, py + ph * 0.55)
      .lineTo(px + pw - 3, py + ph * 0.55)
      .stroke({ width: 1.2, color: LIFT_PARTS.brass });
    return;
  }
  if (pw < 12) return;
  // The sunburst: rays from the seam, low, up to the top of the panel.
  const seam = side === 'left' ? px + pw - 2 : px + 2;
  const far = side === 'left' ? px + 2 : px + pw - 2;
  const hub = py + ph * 0.42;
  for (let k = 0; k < 4; k += 1) {
    const t = k / 3;
    g.moveTo(seam, hub)
      .lineTo(seam + (far - seam) * t, py + 3 + (k === 3 ? ph * 0.16 : 0))
      .stroke({ width: 1.2, color: LIFT_PARTS.brass });
  }
}

/** The station's: wide doors with a hazard edge at the seam, then a window, then glass above. */
function paintWidePanel(
  g: Graphics,
  px: number,
  py: number,
  pw: number,
  ph: number,
  tier: number,
  side: 'left' | 'right',
): void {
  if (tier >= 2 && pw >= 12) {
    g.rect(px + 3, py + 3, pw - 6, ph * 0.42)
      .fill({ color: LIFT_PARTS.glass })
      .stroke({ width: 1, color: COLORS.ink, alpha: 0.6 });
  } else if (tier === 1 && pw >= 12) {
    g.roundRect(px + pw * 0.25, py + ph * 0.14, pw * 0.4, ph * 0.3, 2)
      .fill({ color: LIFT_PARTS.glass })
      .stroke({ width: 1, color: COLORS.ink, alpha: 0.6 });
  }
  // Yellow and ink at the meeting edge, where people step through two abreast.
  const band = Math.min(5, pw * 0.3);
  const bx = side === 'left' ? px + pw - band : px;
  g.rect(bx, py, band, ph).fill({ color: LIFT_PARTS.glow });
  for (let y = py + 2; y < py + ph - 2; y += 6) {
    g.moveTo(bx, y + 3)
      .lineTo(bx + band, y)
      .stroke({ width: 1.6, color: COLORS.ink, alpha: 0.75 });
  }
}

/** The skyscraper's: glass in a frame, then more glass, then frameless with a cool light at the seam. */
function paintGlassPanel(
  g: Graphics,
  px: number,
  py: number,
  pw: number,
  ph: number,
  tier: number,
  side: 'left' | 'right',
): void {
  const inset = tier <= 0 ? Math.max(2.5, pw * 0.18) : 2;
  g.rect(px + inset, py + inset, pw - inset * 2, ph - inset * 2)
    .fill({ color: LIFT_PARTS.glass })
    .stroke({ width: tier <= 0 ? 1.5 : 1, color: LIFT_PARTS.steelDark });
  // A glint, so it reads as glass and not as a pale panel.
  g.moveTo(px + inset + 2, py + inset + ph * 0.3)
    .lineTo(px + inset + Math.min(pw * 0.4, 8), py + inset + 2)
    .stroke({ width: 1.2, color: COLORS.cabin, alpha: 0.9 });
  if (tier >= 2) {
    const seam = side === 'left' ? px + pw - 1.5 : px + 1.5;
    g.moveTo(seam, py + 2)
      .lineTo(seam, py + ph - 2)
      .stroke({ width: 2, color: LIFT_PARTS.light });
  }
}

/** The best cabin's trim, just inside its outline, in its building's finish. */
export function paintCabinTrim(
  g: Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  tier: number,
  look: LiftLook = 'steel',
): void {
  if (tier < 2) return;
  const colour =
    look === 'glass' ? LIFT_PARTS.light : look === 'wide' ? LIFT_PARTS.glow : LIFT_PARTS.brass;
  g.roundRect(x + 3, y + 3, width - 6, height - 6, 7).stroke({ width: 1.5, color: colour });
  if (look === 'brass' || tier >= 3) {
    g.roundRect(x + 5.5, y + 5.5, width - 11, height - 11, 5).stroke({
      width: 0.8,
      color: LIFT_PARTS.brass,
    });
  }
  // Tiers 4–5 (ADR-0033): lamps in the top corners, then a glow around the car.
  if (tier >= 4) {
    g.circle(x + 7, y + 7, 1.8).fill({ color: LIFT_PARTS.glow });
    g.circle(x + width - 7, y + 7, 1.8).fill({ color: LIFT_PARTS.glow });
  }
  if (tier >= 5) {
    g.roundRect(x - 2, y - 2, width + 4, height + 4, 10).stroke({
      width: 2,
      color: LIFT_PARTS.glow,
      alpha: 0.5,
    });
  }
}
